/** @format */

import React, {ComponentType, PropsWithChildren} from 'react'

import {FieldTypeKey} from './FieldType'

export function getIconByFieldType(
  fieldType: FieldTypeKey,
): ComponentType<IconProps> {
  switch (fieldType) {
    case 'text':
      return TextIcon
    case 'numeric':
      return NumericIcon
    case 'email':
      return EmailIcon
    case 'url':
      return WorldIcon
    case 'datetime-local':
      return CalendarIcon
    case 'file':
      return DocumentIcon
    default:
      const exhaustiveCheck: never = fieldType
      throw new Error(`Unhandled fieldType="${exhaustiveCheck}"`)
  }
}

type IconProps = {
  className?: string
}

function SvgWrapper(props: PropsWithChildren<IconProps>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    />
  )
}

function TextIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="M10.813 13.053a.618.618 0 0 0-1.062 0l-3.264 6.564a.6.6 0 0 0 .488.866.591.591 0 0 0 .574-.333l1.02-2.056h3.422l1.023 2.056a.595.595 0 0 0 .798.271.595.595 0 0 0 .264-.804l-3.263-6.564ZM9.163 16.9l1.116-2.246 1.118 2.246H9.163Zm10.915-2.41a.593.593 0 0 0-.594.594v.016a2.934 2.934 0 0 0-3.88.26 2.978 2.978 0 0 0 0 4.207 2.934 2.934 0 0 0 3.88.26v.017a.595.595 0 0 0 1.014.42.596.596 0 0 0 .174-.42v-4.76a.596.596 0 0 0-.594-.595Zm-2.375 4.759a1.778 1.778 0 0 1-1.645-1.102 1.788 1.788 0 0 1 1.298-2.434 1.779 1.779 0 0 1 1.828.76 1.788 1.788 0 0 1-.221 2.253 1.78 1.78 0 0 1-1.26.523Z" />
      <path d="M19 0H5a5.006 5.006 0 0 0-5 5v14a5.006 5.006 0 0 0 5 5h14a5.006 5.006 0 0 0 5-5V5a5.006 5.006 0 0 0-5-5Zm3 19a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14Z" />
    </SvgWrapper>
  )
}

function NumericIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="M20.672 17.906a2.58 2.58 0 0 1-2.578 2.578H16.16a.644.644 0 1 1 0-1.289h1.934a1.29 1.29 0 0 0 0-2.578h-1.29a.644.644 0 1 1 0-1.289h1.29a.645.645 0 0 0 0-1.289H16.16a.644.644 0 1 1 0-1.289h1.934c1.066 0 1.933.867 1.933 1.934 0 .438-.152.838-.399 1.163a2.568 2.568 0 0 1 1.044 2.06Zm-6.516 1.934a.644.644 0 0 0-.645-.644h-3.08c.274-.239.689-.464 1.122-.7 1.13-.616 2.678-1.46 2.6-3.307-.057-1.368-1.188-2.439-2.574-2.439a2.58 2.58 0 0 0-2.577 2.578.644.644 0 1 0 1.288 0c0-.71.578-1.289 1.289-1.289.682 0 1.259.54 1.287 1.204.04.975-.701 1.452-1.93 2.12-.637.348-1.24.677-1.648 1.159-.306.36-.374.85-.176 1.278.196.422.607.684 1.074.684h3.326a.644.644 0 0 0 .644-.644Zm-6.703 0v-6.445a.645.645 0 0 0-1.098-.455l-1.573 1.575a.645.645 0 0 0 .91.91l.474-.474v4.889a.644.644 0 1 0 1.287 0Z" />
      <path d="M19 0H5a5.006 5.006 0 0 0-5 5v14a5.006 5.006 0 0 0 5 5h14a5.006 5.006 0 0 0 5-5V5a5.006 5.006 0 0 0-5-5Zm3 19a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14Z" />
    </SvgWrapper>
  )
}

function EmailIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="M19,1H5A5.006,5.006,0,0,0,0,6V18a5.006,5.006,0,0,0,5,5H19a5.006,5.006,0,0,0,5-5V6A5.006,5.006,0,0,0,19,1ZM5,3H19a3,3,0,0,1,2.78,1.887l-7.658,7.659a3.007,3.007,0,0,1-4.244,0L2.22,4.887A3,3,0,0,1,5,3ZM19,21H5a3,3,0,0,1-3-3V7.5L8.464,13.96a5.007,5.007,0,0,0,7.072,0L22,7.5V18A3,3,0,0,1,19,21Z" />
    </SvgWrapper>
  )
}

function WorldIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0ZM22,12a9.938,9.938,0,0,1-1.662,5.508l-1.192-1.193A.5.5,0,0,1,19,15.962V15a3,3,0,0,0-3-3H13a1,1,0,0,1-1-1v-.5a.5.5,0,0,1,.5-.5A2.5,2.5,0,0,0,15,7.5v-1a.5.5,0,0,1,.5-.5h1.379a2.516,2.516,0,0,0,1.767-.732l.377-.377A9.969,9.969,0,0,1,22,12Zm-19.951.963,3.158,3.158A2.978,2.978,0,0,0,7.329,17H10a1,1,0,0,1,1,1v3.949A10.016,10.016,0,0,1,2.049,12.963ZM13,21.949V18a3,3,0,0,0-3-3H7.329a1,1,0,0,1-.708-.293L2.163,10.249A9.978,9.978,0,0,1,17.456,3.63l-.224.224A.507.507,0,0,1,16.879,4H15.5A2.5,2.5,0,0,0,13,6.5v1a.5.5,0,0,1-.5.5A2.5,2.5,0,0,0,10,10.5V11a3,3,0,0,0,3,3h3a1,1,0,0,1,1,1v.962a2.516,2.516,0,0,0,.732,1.767l1.337,1.337A9.971,9.971,0,0,1,13,21.949Z" />
    </SvgWrapper>
  )
}

function CalendarIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="M19,2H18V1a1,1,0,0,0-2,0V2H8V1A1,1,0,0,0,6,1V2H5A5.006,5.006,0,0,0,0,7V19a5.006,5.006,0,0,0,5,5H19a5.006,5.006,0,0,0,5-5V7A5.006,5.006,0,0,0,19,2ZM2,7A3,3,0,0,1,5,4H19a3,3,0,0,1,3,3V8H2ZM19,22H5a3,3,0,0,1-3-3V10H22v9A3,3,0,0,1,19,22Z" />
      <circle cx="12" cy="15" r="1.5" />
      <circle cx="7" cy="15" r="1.5" />
      <circle cx="17" cy="15" r="1.5" />
    </SvgWrapper>
  )
}

function DocumentIcon(props: IconProps) {
  return (
    <SvgWrapper {...props}>
      <path d="m17 14a1 1 0 0 1 -1 1h-8a1 1 0 0 1 0-2h8a1 1 0 0 1 1 1zm-4 3h-5a1 1 0 0 0 0 2h5a1 1 0 0 0 0-2zm9-6.515v8.515a5.006 5.006 0 0 1 -5 5h-10a5.006 5.006 0 0 1 -5-5v-14a5.006 5.006 0 0 1 5-5h4.515a6.958 6.958 0 0 1 4.95 2.05l3.484 3.486a6.951 6.951 0 0 1 2.051 4.949zm-6.949-7.021a5.01 5.01 0 0 0 -1.051-.78v4.316a1 1 0 0 0 1 1h4.316a4.983 4.983 0 0 0 -.781-1.05zm4.949 7.021c0-.165-.032-.323-.047-.485h-4.953a3 3 0 0 1 -3-3v-4.953c-.162-.015-.321-.047-.485-.047h-4.515a3 3 0 0 0 -3 3v14a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3z" />
    </SvgWrapper>
  )
}
